import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useOutletContext } from 'react-router-dom';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Loader2, Users, Mail, CheckCircle2, XCircle, ShieldOff,
  MessageCircle, Clock, Sparkles, ArrowRight, X,
} from 'lucide-react';
import {
  fetchOrgOverview, fetchOrgConversationStats, fetchInsights,
} from '@/lib/admin/admin-api';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { WelcomeState } from '@/components/admin/WelcomeState';
import { emit, emitFirstLoginOnce } from '@/lib/admin/telemetry';
import { getUserSetting, setUserSetting } from '@/lib/admin/user-settings';
import { cn } from '@/lib/utils';

function formatDuration(seconds: number | null): string {
  if (seconds == null) return '—';
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r === 0 ? `${m}m` : `${m}m ${r}s`;
}

const DISMISS_KEY = 'welcome_done_dismissed';
function legacyLsKey(kind: string, userId: string, orgId: string) {
  return `cl.${kind}.${userId}.${orgId}`;
}

export default function OrgOverview() {
  const { orgId } = useOutletContext<{ orgId: string }>();
  const { user } = useAuth();
  const userId = user?.id ?? '';
  const firstName =
    (user?.user_metadata?.first_name as string | undefined) ??
    (user?.email?.split('@')[0] ?? 'there');

  const overviewQ = useQuery({
    queryKey: ['admin', 'overview', orgId],
    queryFn: () => fetchOrgOverview(orgId),
  });
  const convQ = useQuery({
    queryKey: ['admin', 'conv-stats', orgId],
    queryFn: () => fetchOrgConversationStats(orgId),
  });
  const surveysQ = useQuery({
    queryKey: ['admin', 'survey-count', orgId],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('surveys')
        .select('id', { count: 'exact', head: true })
        .eq('organization_id', orgId);
      if (error) throw error;
      return count ?? 0;
    },
  });
  const insightsQ = useQuery({
    queryKey: ['admin', 'insights-overview', orgId],
    queryFn: () => fetchInsights(orgId, { limit: 50 }),
  });

  // First-login telemetry (once per user+org, persisted in telemetry_events)
  useEffect(() => {
    if (!userId || !orgId) return;
    emitFirstLoginOnce(userId, orgId);
  }, [userId, orgId]);

  // Dismissal of "all set" banner — persisted in user_settings, cross-device.
  const [dismissed, setDismissed] = useState<boolean>(false);
  useEffect(() => {
    if (!userId || !orgId) return;
    let cancelled = false;
    (async () => {
      const remote = await getUserSetting<Record<string, boolean>>(userId, DISMISS_KEY);
      let isDismissed = !!remote?.[orgId];
      // One-time migration from localStorage
      if (typeof window !== 'undefined') {
        const legacy = localStorage.getItem(legacyLsKey('welcome_done_dismissed', userId, orgId));
        if (legacy && !isDismissed) {
          isDismissed = true;
          const merged = { ...(remote ?? {}), [orgId]: true };
          await setUserSetting(userId, DISMISS_KEY, merged);
        }
        if (legacy) {
          localStorage.removeItem(legacyLsKey('welcome_done_dismissed', userId, orgId));
        }
      }
      if (!cancelled) setDismissed(isDismissed);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, orgId]);

  const o = overviewQ.data;
  const cs = convQ.data;
  const surveyCount = surveysQ.data ?? 0;
  const insights = insightsQ.data ?? [];

  const topTheme = useMemo(() => {
    if (!insights.length) return null;
    const counts = new Map<string, number>();
    insights.forEach((i) => counts.set(i.theme, (counts.get(i.theme) ?? 0) + 1));
    let best: { theme: string; n: number } | null = null;
    counts.forEach((n, theme) => {
      if (!best || n > best.n) best = { theme, n };
    });
    return best;
  }, [insights]);

  const totalConversations = cs?.total_conversations ?? 0;

  if (overviewQ.isLoading) {
    return <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />;
  }
  if (overviewQ.error || !o) {
    return <p className="text-sm text-destructive">{(overviewQ.error as Error)?.message ?? 'No data'}</p>;
  }

  const steps = {
    invite: (o.invite_invited ?? 0) > 0 || (o.employee_count ?? 0) > 1,
    conversation: surveyCount > 0,
    theme: insights.length > 0,
  };
  const allDone = steps.invite && steps.conversation && steps.theme;

  const isEmptyOrg =
    totalConversations === 0 &&
    (o.invite_invited ?? 0) === 0 &&
    (o.employee_count ?? 0) <= 1;

  // Single CTA above KPI grid
  const cta = (() => {
    const pending = (o.invite_invited ?? 0) - (o.invite_accepted ?? 0);
    if (pending > 0) {
      return {
        label: `Resend pending invites (${pending})`,
        to: '/app/admin/people',
        key: 'resend_invites' as const,
      };
    }
    if (totalConversations === 0) {
      return {
        label: 'Re-engage your team',
        to: '/app/admin/surveys',
        key: 'reengage' as const,
      };
    }
    return {
      label: "View this week's insights",
      to: '/app/admin/insights',
      key: 'view_insights' as const,
    };
  })();

  const stats = [
    { label: 'Employees', value: o.employee_count, icon: Users },
    { label: 'Invited', value: o.invite_invited, icon: Mail },
    { label: 'Accepted', value: o.invite_accepted, icon: CheckCircle2 },
    { label: 'Expired', value: o.invite_expired, icon: XCircle },
  ];

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h2 className="text-2xl font-heading font-semibold">Organization overview</h2>
        <p className="text-sm text-muted-foreground">
          Aggregate-only. No individual responses are accessible.
        </p>
      </div>

      {isEmptyOrg && !allDone && (
        <WelcomeState firstName={firstName} orgId={orgId} userId={userId} steps={steps} />
      )}

      {allDone && !dismissed && (
        <Card className="border-primary/40 bg-primary/5">
          <CardContent className="p-4 flex items-center gap-3">
            <Sparkles className="h-5 w-5 text-primary" />
            <div className="flex-1">
              <p className="text-sm font-medium">You're all set.</p>
              <p className="text-xs text-muted-foreground">
                Your team is invited, conversations are running, and themes are live.
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Dismiss"
              onClick={async () => {
                const existing = (await getUserSetting<Record<string, boolean>>(userId, DISMISS_KEY)) ?? {};
                await setUserSetting(userId, DISMISS_KEY, { ...existing, [orgId]: true });
                setDismissed(true);
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      )}

      {!isEmptyOrg && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card p-3">
          <p className="text-sm text-muted-foreground">{cta.label}</p>
          <Button
            asChild
            size="sm"
            onClick={() =>
              emit({
                name: 'overview_cta_click',
                cta: cta.key,
                org_id: orgId,
                user_id: userId,
                timestamp: new Date().toISOString(),
              })
            }
          >
            <Link to={cta.to}>
              Go <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </Button>
        </div>
      )}

      <div className={cn('grid grid-cols-2 md:grid-cols-4 gap-3', isEmptyOrg && 'opacity-50')}>
        {stats.map((s) => (
          <Card key={s.label}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <s.icon className="h-3.5 w-3.5" />
                {s.label}
              </div>
              <div className="mt-1 text-2xl font-semibold tabular-nums">{s.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className={cn('grid grid-cols-1 md:grid-cols-4 gap-3', isEmptyOrg && 'opacity-50')}>
        <Card className="md:col-span-1">
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground">Pulse avg (7d)</div>
            <div className="mt-1 text-2xl font-semibold tabular-nums">
              {o.last_7d_suppressed || o.last_7d_org_avg == null
                ? '—'
                : o.last_7d_org_avg.toFixed(2)}
            </div>
          </CardContent>
        </Card>
        <Card className="md:col-span-1">
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground">Avg calls / employee</div>
            <div className="mt-1 text-2xl font-semibold tabular-nums">
              {cs?.suppressed || cs?.avg_conversations_per_employee == null
                ? '—'
                : cs.avg_conversations_per_employee.toFixed(1)}
            </div>
          </CardContent>
        </Card>
        <Card className="md:col-span-1">
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground">Active talkers / employees</div>
            <div className="mt-1 text-2xl font-semibold tabular-nums">
              {cs?.suppressed ? '—' : `${cs?.active_employee_count ?? 0} / ${o.employee_count ?? 0}`}
            </div>
          </CardContent>
        </Card>
        <Card className="md:col-span-1">
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground">Top theme this week</div>
            <div className="mt-1 text-sm font-semibold">
              {totalConversations < 5 ? (
                <span className="text-xs text-muted-foreground">
                  Need 5+ conversations to unlock
                </span>
              ) : topTheme ? (
                <span className="truncate block" title={topTheme.theme}>
                  {topTheme.theme}
                </span>
              ) : (
                '—'
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base font-heading">Last 7 days — pulse average</CardTitle>
          <CardDescription>k ≥ 5 floor enforced server-side.</CardDescription>
        </CardHeader>
        <CardContent>
          {o.last_7d_suppressed ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <ShieldOff className="h-4 w-4" />
              Suppressed — fewer than 5 employees responded in the last 7 days.
            </div>
          ) : (
            <div className="flex items-baseline gap-3">
              <span className="text-4xl font-semibold tabular-nums">
                {o.last_7d_org_avg?.toFixed(2)}
              </span>
              <span className="text-sm text-muted-foreground">
                avg · {o.last_7d_n} unique employees
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base font-heading">
            Charles conversations — per-employee averages
          </CardTitle>
          <CardDescription>
            Real conversations only (ended or with at least one spoken turn). k ≥ 5 floor enforced.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {convQ.isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : !cs ? (
            <p className="text-sm text-destructive">
              {(convQ.error as Error)?.message ?? 'No data'}
            </p>
          ) : cs.suppressed ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <ShieldOff className="h-4 w-4" />
              Suppressed — fewer than 5 employees have had a real conversation yet.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <MessageCircle className="h-3.5 w-3.5" />
                  Avg calls / employee
                </div>
                <div className="mt-1 text-3xl font-semibold tabular-nums">
                  {cs.avg_conversations_per_employee?.toFixed(1) ?? '—'}
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Clock className="h-3.5 w-3.5" />
                  Avg call length
                </div>
                <div className="mt-1 text-3xl font-semibold tabular-nums">
                  {formatDuration(cs.avg_conversation_seconds)}
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Users className="h-3.5 w-3.5" />
                  Active talkers · total calls
                </div>
                <div className="mt-1 text-3xl font-semibold tabular-nums">
                  {cs.active_employee_count}
                  <span className="text-base font-normal text-muted-foreground">
                    {' '}
                    · {cs.total_conversations}
                  </span>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
